import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import ServiceSection from "@/components/ServiceSection";
import SEO from "@/components/SEO";
import FAQSection from "@/components/FAQSection";
import { 
  Shield, 
  Video, 
  Palette, 
  Camera, 
  Globe, 
  MessageSquare,
  Lightbulb,
  AlertTriangle,
  CheckCircle,
  Award,
  Play
} from "lucide-react";

const HealthSafetyPage = () => {
  const services = [
    {
      icon: Lightbulb,
      title: "Strategy & Risk Assessment",
      description: "We work with your H&S team to identify communication gaps, prioritise hazards, and develop content strategies that address your specific risks. Data-driven planning for maximum impact.",
      services: [
        "Safety communication audit",
        "Risk priority mapping",
        "Content strategy development",
        "Behavioural insight analysis",
        "Campaign effectiveness planning"
      ],
      image: "https://customer-assets.emergentagent.com/job_2f219309-49d2-4096-9b1f-9aa2825973b1/artifacts/19n82sqg_IMG_1351.jpg",
      altText: "Health and safety strategy and risk assessment planning for construction site"
    },
    {
      icon: Video,
      title: "Video Production",
      description: "Cinema-quality safety videos that people actually watch. From dramatic incident reconstructions to clear procedural demonstrations, we create content that changes behaviour.",
      services: [
        "Safety induction videos",
        "Incident awareness films",
        "Procedure demonstrations",
        "Toolbox talk content",
        "Animated hazard explainers"
      ],
      image: "https://customer-assets.emergentagent.com/job_2f219309-49d2-4096-9b1f-9aa2825973b1/artifacts/lp7hbwda_DSC_0097.jpg",
      altText: "Health and safety video production filming on construction site"
    },
    {
      icon: Palette,
      title: "Graphic Design",
      description: "High-visibility safety communications that cut through the noise. Signage, posters, and visual standards that reinforce safety messages at every touchpoint.",
      services: [
        "Safety signage systems",
        "Poster and banner campaigns",
        "PPE and procedure graphics",
        "Safety manual design",
        "Visual standard guidelines"
      ],
      image: "https://customer-assets.emergentagent.com/job_2f219309-49d2-4096-9b1f-9aa2825973b1/artifacts/gn1e5krq_health%20and%20safety%20graphic%20design.jpg",
      altText: "Health and safety graphic design for workplace safety signage and communications"
    },
    {
      icon: Camera,
      title: "Professional Photography",
      description: "Generic stock photos undermine safety messaging — your workforce can spot them instantly. We photograph your actual sites, your real teams, and your genuine working conditions, so every image carries credibility.",
      services: [
        "PPE demonstration imagery",
        "Correct procedure documentation",
        "Site hazard photography",
        "Before and after captures",
        "Incident reconstruction stills"
      ],
      image: "https://customer-assets.emergentagent.com/job_people-comms-hub/artifacts/h30gr4yz_IMG_0060%20%282%29%20%281%29_800x533.jpg",
      altText: "Real employee safety photography for health and safety training materials"
    },
    {
      icon: MessageSquare,
      title: "Content Creation",
      description: "Clear, accessible safety content that everyone understands. Scripts, signage copy, and documentation that transcends language barriers and literacy levels.",
      services: [
        "Safety video scripts",
        "Signage and label copywriting",
        "Procedure documentation",
        "Multi-language adaptation",
        "Assessment content creation"
      ],
      image: "https://customer-assets.emergentagent.com/job_2f219309-49d2-4096-9b1f-9aa2825973b1/artifacts/yojq4hcd_IMG_4551.JPG",
      altText: "Health and safety content creation including toolbox talks and safety documentation"
    },
    {
      icon: Globe,
      title: "Digital Safety Platforms",
      description: "Dedicated safety portals and apps that make compliance easy. Track completions, push alerts, and give your workforce instant access to critical information.",
      services: [
        "Safety portal development",
        "Mobile safety apps",
        "Digital permit systems",
        "Incident reporting tools",
        "Compliance tracking dashboards"
      ],
      image: "https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=800",
      altText: "Health and safety compliance portal and digital reporting platform development"
    }
  ];

  const benefits = [
    {
      icon: AlertTriangle,
      title: "Reduced Incidents",
      description: "Engaging safety content changes behaviour in ways that PowerPoint presentations never will."
    },
    {
      icon: CheckCircle,
      title: "Improved Compliance",
      description: "Clear, accessible materials make it easy for everyone to understand and follow procedures."
    },
    {
      icon: Award,
      title: "Industry Recognition",
      description: "Award-worthy safety campaigns that demonstrate your commitment to workforce wellbeing."
    }
  ];

  return (
    <div data-testid="health-safety-page" className="min-h-screen bg-[#050505]">
      <SEO
        title="Health & Safety Videos & Content for Construction and Industry"
        description="Professional health and safety video production for large organisations. Safety induction films, incident awareness content, toolbox talks and behavioural safety campaigns that reduce incidents and improve compliance across your workforce."
        keywords="health and safety videos, health and safety content, safety training videos, safety induction videos, H&S video production, workplace safety content, construction safety videos, health and safety communications, safety awareness campaigns, toolbox talk content"
        canonical="/health-safety"
      />
      {/* Hero Section */}
      <section className="relative min-h-[70vh] flex items-center" data-testid="safety-hero">
        <div className="absolute inset-0 z-0">
          <img
            src="https://images.unsplash.com/photo-1504307651254-35680f356dfd?w=1920"
            alt="Health and safety training video production on construction site"
            className="w-full h-full object-cover"
            decoding="async"
            fetchpriority="high"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#050505] via-[#050505]/90 to-transparent" />
          <div className="absolute inset-0 hero-overlay" />
        </div>

        <div className="container mx-auto px-4 md:px-8 relative z-10 pt-32">
          <div className="max-w-3xl">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 rounded-lg bg-[#00E5FF]/20 flex items-center justify-center">
                <Shield className="w-6 h-6 text-[#00E5FF]" />
              </div>
              <span className="text-[#00E5FF] font-semibold uppercase tracking-wider">
                Internal Communications
              </span>
            </div>

            <h1 className="font-display text-5xl md:text-7xl tracking-tight leading-none text-white mb-6">
              HEALTH & SAFETY
            </h1>

            <p className="text-lg md:text-xl text-[#B3B3B3] max-w-2xl mb-8">
              Safety content that saves lives. We create impactful health and safety communications 
              for large organisations — engaging your entire workforce and driving genuine behavioural 
              change from head office to the frontline.
            </p>

            <div className="flex flex-col sm:flex-row gap-4">
              <Link to="/contact">
                <Button
                  data-testid="safety-book-call-btn"
                  className="bg-[#00E5FF] text-black hover:bg-[#00B8CC] font-bold px-8 py-6 uppercase tracking-wider text-base"
                >
                  <Play className="w-5 h-5 mr-2 fill-black" />
                  Book a Call
                </Button>
              </Link>
              <Link to="/case-studies">
                <Button
                  data-testid="safety-case-studies-btn"
                  variant="outline"
                  className="bg-white/10 text-white hover:bg-white/20 border-white/10 px-8 py-6 uppercase tracking-wider text-base"
                >
                  View Case Studies
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Problem Statement */}
      <section className="py-10 md:py-16 bg-[#0a0a0a]" data-testid="safety-problem">
        <div className="container mx-auto px-4 md:px-8">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="font-display text-3xl md:text-5xl tracking-wide text-white mb-8">
              BORING SAFETY TRAINING
              <br />
              <span className="text-[#00E5FF]">DOESN'T SAVE LIVES</span>
            </h2>
            <p className="text-[#B3B3B3] text-lg leading-relaxed mb-8">
              Death by PowerPoint. Toolbox talks that nobody listens to. Faded 
              posters that blend into the background. When safety communications 
              fail to engage, your people are at risk. Compliance boxes get ticked 
              while real hazards get ignored.
            </p>
            <p className="text-white text-xl font-medium">
              Your workforce deserves safety content that grabs attention, sticks 
              in memory, and actually changes how people work. That's what we deliver.
            </p>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-10 md:py-16 bg-[#050505]" data-testid="safety-benefits">
        <div className="container mx-auto px-4 md:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {benefits.map((benefit, index) => (
              <div
                key={index}
                className="p-8 bg-[#141414] rounded-lg border border-[#1F1F1F] hover:border-[#00E5FF]/30 transition-colors"
                data-testid={`benefit-${index}`}
              >
                <div className="w-14 h-14 rounded-lg bg-[#00E5FF]/10 flex items-center justify-center mb-6">
                  <benefit.icon className="w-7 h-7 text-[#00E5FF]" />
                </div>
                <h3 className="font-display text-xl tracking-wide text-white mb-3">
                  {benefit.title}
                </h3>
                <p className="text-[#B3B3B3]">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Services */}
      <section className="py-10 md:py-16 bg-[#0a0a0a]" data-testid="safety-services">
        <div className="container mx-auto px-4 md:px-8">
          <div className="text-center mb-16">
            <h2 className="font-display text-4xl md:text-5xl tracking-wide text-white mb-4">
              WHAT WE DELIVER
            </h2>
            <p className="text-[#B3B3B3] text-lg max-w-2xl mx-auto">
              Comprehensive H&S communications that protect your people and 
              demonstrate your commitment to their wellbeing.
            </p>
          </div>

          <div className="space-y-20">
            {services.map((service, index) => (
              <ServiceSection
                key={index}
                {...service}
                reverse={index % 2 === 1}
                accentColor="#00E5FF"
              />
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <FAQSection faqs={[
        { question: "Why should health and safety training use video?", answer: "Video brings safety scenarios to life in a way written materials cannot. Employees can see real examples of correct procedures, understand consequences of non-compliance, and retain information far more effectively. Our safety videos are filmed on real sites with real workers for maximum authenticity and impact." },
        { question: "What types of health and safety content do you create?", answer: "We produce safety induction films, incident awareness videos, toolbox talk content, procedure demonstrations, behavioural safety campaigns, site-specific safety guides and digital safety communications — all tailored to your industry and workforce." },
        { question: "How do you ensure safety content reaches frontline workers?", answer: "We design content for multi-channel delivery including site screens, mobile devices, toolbox sessions and QR-accessible content. Our materials work for desk-based and non-desk workers alike, using clear language and visual storytelling that transcends literacy barriers." },
        { question: "Can health and safety videos reduce workplace incidents?", answer: "Yes. Engaging, well-produced safety content significantly improves awareness, understanding and behavioural compliance. When employees can see and relate to realistic scenarios featuring people like them, the message is far more likely to influence their behaviour on site." }
      ]} />

      <section className="py-20 md:py-32 bg-[#050505] relative overflow-hidden" data-testid="safety-cta">
        <div className="absolute inset-0 opacity-20">
          <div className="absolute top-0 left-0 w-96 h-96 bg-[#00E5FF] rounded-full blur-[150px]" />
        </div>
        
        <div className="container mx-auto px-4 md:px-8 relative z-10">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="font-display text-4xl md:text-5xl tracking-wide text-white mb-6">
              READY TO PROTECT
              <br />
              <span className="text-[#00E5FF]">YOUR PEOPLE?</span>
            </h2>
            <p className="text-[#B3B3B3] text-lg mb-8">
              Let's discuss how we can help create safety communications that 
              actually work - because your people's lives depend on it.
            </p>
            <Link to="/contact">
              <Button
                data-testid="safety-cta-btn"
                className="bg-[#00E5FF] text-black hover:bg-[#00B8CC] font-bold px-10 py-6 uppercase tracking-wider text-base"
              >
                Start Protecting Your Team
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HealthSafetyPage;
